import { useState, useEffect, useCallback } from 'react';
import userManagementApi from '../../services/admin/userManagementApi';
import { useToast } from '@chakra-ui/react';

/**
 * ユーザー管理機能のためのカスタムフック
 */
const useUserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const toast = useToast();

  // ユーザー一覧を取得
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await userManagementApi.getUsers();
      setUsers(data);
    } catch (err) {
      setError(err.message || '取得中にエラーが発生しました');
      toast({
        title: 'エラー',
        description: 'ユーザー一覧の取得に失敗しました',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // 新規ユーザー作成
  const createUser = useCallback(async (userData) => {
    try {
      setLoading(true);
      setError(null);
      const newUser = await userManagementApi.createUser(userData);
      setUsers(prev => [...prev, newUser]);
      toast({
        title: 'ユーザーを作成しました',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      return newUser;
    } catch (err) {
      setError(err.message || '作成中にエラーが発生しました');
      toast({
        title: 'エラー',
        description: 'ユーザーの作成に失敗しました',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      throw err;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // ユーザー更新
  const updateUser = useCallback(async (userId, userData) => {
    try {
      setLoading(true);
      setError(null);
      const updatedUser = await userManagementApi.updateUser(userId, userData);
      setUsers(prev => prev.map(user => user.id === userId ? updatedUser : user));
      toast({
        title: 'ユーザーを更新しました',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      return updatedUser;
    } catch (err) {
      setError(err.message || '更新中にエラーが発生しました');
      toast({
        title: 'エラー',
        description: 'ユーザーの更新に失敗しました',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      throw err;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // ユーザー削除
  const deleteUser = useCallback(async (userId) => {
    try {
      setLoading(true);
      setError(null);
      await userManagementApi.deleteUser(userId);
      setUsers(prev => prev.filter(user => user.id !== userId));
      toast({
        title: 'ユーザーを削除しました',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      setError(err.message || '削除中にエラーが発生しました');
      toast({
        title: 'エラー',
        description: 'ユーザーの削除に失敗しました',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      throw err;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // 初期読み込み
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return {
    users,
    loading,
    error,
    fetchUsers,
    createUser,
    updateUser,
    deleteUser
  };
};

export default useUserManagement;